import {
  CheckCircleFilled,
  CompassFilled,
  DisconnectOutlined,
  EllipsisOutlined,
  FileDoneOutlined,
  FlagOutlined,
  HomeOutlined,
  LinkOutlined,
  LoadingOutlined,
  LockOutlined,
  SendOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { useOrders } from '@api/useOrders'
import credentialOff from '@assets/CustomIcons/certificate-off.png'
import driverTractorIconLight from '@assets/CustomIcons/driverTractor.svg'
import driverTractorIconDark from '@assets/CustomIcons/driverTractorDark.svg'
import driverTrailerIconLight from '@assets/CustomIcons/driverTrailer.svg'
import driverTrailerIconDark from '@assets/CustomIcons/driverTrailerDark.svg'
import { BBDTag, GraviButton, Horizontal, Texto, Vertical, useThemeContext } from '@gravitate-js/excalibrr'
import { IconClockDown, IconClockExclamation, IconClockPause, IconClockStop, IconClockUp } from '@tabler/icons-react'
import { formatDateStringToTime, getTimeDisplayPreference, titleCase } from '@utils/general'
import { Button, Col, Dropdown, Form, Menu, MenuProps, Popover, Row, Tooltip } from 'antd'
import moment from 'moment'
import { useCallback, useMemo, useState } from 'react'

import { useLockedSchedules } from '@hooks/useLockedSchedules'
import {
  DRIVER_UTILIZATION_COLORS,
  DRIVER_UTILIZATION_DARK_COLORS,
  DRIVER_UTILIZATION_STATUS,
  getLoadedPercent,
} from '@pages/OrderManagement/util'
import { durationFormatter } from '@utils/cell_formatter'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Link, createSearchParams, useNavigate } from 'react-router-dom'
import { useSecurityContext } from '../../../Navigation/SecurityContext'
import { EquipmentChangeForm } from './components/EquipmentChangePopover'
import { HoursOfServicePopover } from './components/HoursOfServicePopover'
import { StatusStampOffline } from './components/StatusStampOffline'
import { TractorIcon, TrailerIcon } from '@/components/_SiteWide/TruckIcons'

function DriverUtilizationIcon({ status }) {
  if (status === DRIVER_UTILIZATION_STATUS.underutilized)
    return <IconClockDown className='driver-utilization-icon warning' data-testid='driver-underutilized' size={16} />
  if (status === DRIVER_UTILIZATION_STATUS.close_to_max)
    return (
      <IconClockExclamation className='driver-utilization-icon warning' size={16} data-testid='driver-close-to-max' />
    )
  if (status === DRIVER_UTILIZATION_STATUS.overutilized)
    return <IconClockUp className='driver-utilization-icon error' size={16} data-testid='driver-overutilized' />
  return null
}

export function NamePlate({
  siteConfig,
  schedule,
  linkWizardActive,
  showingDrawer,
  setShowingDSUDrawer,
  linkAction,
  setStatusUpdateRequest,
  trailersWithConfigs,
  tractorNumbers,
  handleEquipmentChange,
}) {
  const { sendCarrierOrderMutation } = useOrders()

  const unsentIds = useMemo(
    () =>
      schedule.trips
        .filter((t) => !!t.order_id && !['sent', 'pending'].includes(t.carrier_notify_state))
        .map((t) => t.order_id),
    [schedule.trips]
  )

  const handleCarrierSend = useCallback(() => {
    if (unsentIds.length) {
      sendCarrierOrderMutation.mutate({
        schedule_id: schedule.id,
        order_ids: unsentIds,
        force: false,
        instant: true,
      })
    }
  }, [schedule, unsentIds, sendCarrierOrderMutation])

  return (
    <NamePlateView
      siteConfig={siteConfig}
      linkAction={linkAction}
      tripStatus={schedule.status}
      driverDelay={schedule.delay}
      driverBreak={schedule?.driver_break}
      isOnline={schedule.is_online}
      hoursAssigned={parseFloat(schedule.hours_assigned || '0').toFixed(1)}
      hoursAvail={parseFloat(schedule.hours_available).toFixed(1)}
      shiftStart={formatDateStringToTime(schedule.shift_start, schedule.shift_start_local)}
      shiftEnd={formatDateStringToTime(schedule.shift_end, schedule.shift_end_local)}
      driverName={schedule.driver_name}
      homeLocation={schedule.home_location}
      loadedMiles={schedule.loaded_miles}
      totalMiles={schedule.total_miles}
      utilization={schedule.driver_utilization}
      linkWizardActive={linkWizardActive}
      showingDrawer={showingDrawer}
      setShowingDSUDrawer={setShowingDSUDrawer}
      schedule={schedule}
      setStatusUpdateRequest={setStatusUpdateRequest}
      onCarrierSend={handleCarrierSend}
      unsentIds={unsentIds}
      sendLoading={sendCarrierOrderMutation.isPending}
      handleEquipmentChange={handleEquipmentChange}
      tractorNumbers={tractorNumbers}
      trailersWithConfigs={trailersWithConfigs}
    />
  )
}

function EquipmentChangePopover({ tractorNumbers, trailersWithConfigs, handleEquipmentChange, schedule }) {
  const [equipmentChangeVisible, setEquipmentChangeVisible] = useState(false)
  return (
    <Popover
      title={
        <Horizontal justifyContent='space-between' style={{ width: '100%' }} verticalCenter>
          <Texto weight={'bold'}>Shift Tractor + Trailer</Texto>
          <Texto>{schedule.driver_name}</Texto>
        </Horizontal>
      }
      content={
        <EquipmentChangeForm
          handleClose={() => setEquipmentChangeVisible(false)}
          handleEquipmentChange={handleEquipmentChange}
          trailersWithConfigs={trailersWithConfigs}
          tractorNumbers={tractorNumbers}
          schedule={schedule}
        />
      }
      trigger='click'
      open={equipmentChangeVisible}
      onOpenChange={() => setEquipmentChangeVisible(!equipmentChangeVisible)}
      styles={{ body: { width: 300 } }}
      align={{ offset: [0, 32] }}
      destroyTooltipOnHide
    >
      <GraviButton appearance='link' buttonText='Shift Tractor + Trailer' style={{ padding: 0 }} />
    </Popover>
  )
}

function NamePlateView({
  siteConfig,
  linkAction,
  tripStatus,
  driverDelay,
  driverBreak,
  isOnline,
  loadedMiles,
  totalMiles,
  homeLocation,
  driverName,
  shiftStart,
  shiftEnd,
  linkWizardActive,
  setShowingDSUDrawer,
  schedule,
  setStatusUpdateRequest,
  onCarrierSend,
  unsentIds,
  sendLoading,
  handleEquipmentChange,
  tractorNumbers,
  trailersWithConfigs,
}) {
  const { lockedSchedules } = useLockedSchedules()
  const navigate = useNavigate()
  const optimizationState = useMemo(
    () => lockedSchedules?.find((s) => s.id === schedule?.id)?.state,
    [lockedSchedules, schedule]
  )
  const driverStatusMenuItems: MenuProps['items'] = [
    {
      key: 'driver-info',
      onClick: () => {
        window.open(
          `/driver/driver_info?${createSearchParams({
            id: schedule.driver_id,
          }).toString()}`,
          '_blank'
        )
      },
      label: <Texto className='p-2'>Driver Info</Texto>,
    },
    {
      key: 'driver-status-updates',
      onClick: () => {
        setStatusUpdateRequest({
          driver_id: schedule.driver_id,
          shift_start: moment(schedule.shift_start).toISOString(),
          shift_end: moment(schedule.shift_end).toISOString(),
        })
        setShowingDSUDrawer(true)
      },
      label: <Texto className='p-2'>Driver Status Updates</Texto>,
    },
    {
      type: 'divider',
    },
    {
      key: 'shift-tractor-trailer',
      label: (
        <EquipmentChangePopover
          tractorNumbers={tractorNumbers}
          trailersWithConfigs={trailersWithConfigs}
          handleEquipmentChange={handleEquipmentChange}
          schedule={schedule}
        />
      ),
    },
  ]

  const { scopes } = useSecurityContext()
  const canViewDSUReport = scopes.scopes.includes('su')
  const canViewDwellReport = scopes.admin?.location_write
  const isDelayed = tripStatus?.toLowerCase() === 'delayed' && driverDelay
  const isOnBreak = tripStatus?.toLowerCase() === 'on break' && driverBreak
  const showDriverDelayBreakPopup = isDelayed || isOnBreak

  return (
    <div className='name-plate'>
      <div style={{ display: 'flex', flexGrow: 1 }}>
        <Popover
          styles={{ body: { minWidth: 450 } }}
          classNames={{ body: 'bordered' }}
          placement='topLeft'
          content={
            showDriverDelayBreakPopup &&
            getDelayPopoverContent(driverDelay, driverBreak, isOnBreak, canViewDwellReport, navigate)
          }
          title={showDriverDelayBreakPopup && getDelayBreakPopoverTitle(driverDelay, isOnBreak, driverBreak)}
          trigger={showDriverDelayBreakPopup ? 'hover' : 'none'}
        >
          <span className='flex'>
            <StatusStamp
              isOnline={isOnline}
              tripStatus={tripStatus}
              driverDelay={driverDelay}
              isOnBreak={isOnBreak}
              isCarrier={schedule.carrier_id}
              onCarrierSend={onCarrierSend}
              unsentIds={unsentIds}
              sendLoading={sendLoading}
              schedule={schedule}
            />
          </span>
        </Popover>
        <div className='name-plate-inner'>
          <Row className='flex-1' justify='space-between' align='middle'>
            <Texto category='h5' weight='bold'>
              {driverName}
            </Texto>
            <Row align='middle'>
              <Row className='pr-1' align='middle'>
                <HomeOutlined className='home-icon' />
                <Texto category='p2' weight='bold'>
                  {schedule.carrier_name ?? homeLocation}
                </Texto>
              </Row>
              {!linkWizardActive && canViewDSUReport && (
                <Row style={{ width: '15px' }}>
                  <Dropdown menu={{ items: driverStatusMenuItems }} placement='top'>
                    <EllipsisOutlined rotate={90} className='ellipsis-icon' />
                  </Dropdown>
                </Row>
              )}
            </Row>
          </Row>
          {linkWizardActive ? (
            <LinkSelect
              shiftEnd={shiftEnd}
              shiftStart={shiftStart}
              schedule={schedule}
              loadedMiles={loadedMiles}
              totalMiles={totalMiles}
              linkAction={linkAction}
            />
          ) : (
            <NormalDetails
              schedule={schedule}
              shiftEnd={shiftEnd}
              shiftStart={shiftStart}
              optimizationState={optimizationState}
              siteConfig={siteConfig}
              loadedMiles={loadedMiles}
              totalMiles={totalMiles}
              homeLocation={homeLocation}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function NormalDetails({ schedule, shiftStart, shiftEnd, optimizationState, siteConfig }) {
  const { formatDistance } = useFormatter()
  const formattedUnit = formatDistance(null, true)?.toLowerCase()
  const { isDarkTheme } = useThemeContext()
  const { hours_assigned, hours_available, loaded_miles, total_miles } = schedule
  const isBlocked = schedule?.invalid_credentials?.some(
    (cred) => cred.dispatch_impact === 'Block Dispatch' && cred.status === 'Expired' && cred.is_blocked
  )

  const content = useMemo(
    () =>
      schedule?.invalid_credentials?.map((cred) => {
        const key = `${schedule.driver_id}-${cred.credential_id}`

        return (
          <Horizontal style={{ backgroundColor: 'var(--theme-error-dim' }} className={'p-2 pt-1 pb-1'} verticalCenter>
            <img src={credentialOff} alt={'expired credential image'} height={'13px'} />

            <Link to={`/driver/credentials?${new URLSearchParams({ key })}`} target={'_blank'}>
              <Texto className={'ml-2 mr-1'} style={{ textDecoration: 'underline' }}>
                {cred.credential_name}
              </Texto>
            </Link>
            <Texto>{cred.status}</Texto>
          </Horizontal>
        )
      }),
    [schedule?.invalid_credentials]
  )
  const buttonText = useMemo(() => {
    return (
      <Horizontal verticalCenter>
        {isBlocked ? (
          <LockOutlined style={{ color: 'var(--theme-error)' }} />
        ) : (
          <img src={credentialOff} alt={'expired credential image'} height={'13px'} />
        )}
        <Texto className={'ml-2'} category='label' align='right' textTransform='uppercase' weight={'normal'}>
          {shiftStart} - {shiftEnd}
        </Texto>
      </Horizontal>
    )
  }, [isBlocked, shiftStart, shiftEnd])

  return (
    <>
      <Row align='middle'>
        <HoursOfServicePopover
          metrics={schedule.schedule_metrics}
          message={schedule.utilization_message}
          status={schedule.utilization_status}
        >
          <Row align='middle' justify='center' data-testid='driver-utilization-label'>
            <DriverUtilizationIcon status={schedule?.utilization_status} />
            <Texto
              category='h6'
              style={{
                color: isDarkTheme
                  ? DRIVER_UTILIZATION_DARK_COLORS[schedule?.utilization_status]
                  : DRIVER_UTILIZATION_COLORS[schedule?.utilization_status],
              }}
              weight={schedule?.utilization_status === DRIVER_UTILIZATION_STATUS.overutilized ? 700 : 'normal'}
            >
              {parseFloat(hours_assigned || '0').toFixed(1)}
            </Texto>
            <Texto>{`/${hours_available}h`}</Texto>
          </Row>
        </HoursOfServicePopover>
        <Row className='flex-1' justify='space-between'>
          <Row className='pl-2'>
            {schedule.invalid_credentials?.length > 0 ? (
              <Tooltip color={'var(--theme-error-dim)'} title={content}>
                <GraviButton size='small' buttonText={buttonText} className={'invalid-credentials-button'} />
              </Tooltip>
            ) : (
              <Texto category='p1' align='right' style={{ textDecoration: 'underline' }}>
                {shiftStart} - {shiftEnd}
              </Texto>
            )}
          </Row>
        </Row>
        <Row>
          <Tooltip title='(Loaded / Total)'>
            <span>
              <Texto category='p2'>{`${loaded_miles}/${total_miles}${formattedUnit} (${Math.round(
                getLoadedPercent(schedule)
              )}%)`}</Texto>
            </span>
          </Tooltip>
        </Row>
      </Row>
      <Horizontal flex={1}>
        {optimizationState === 'pending' ? (
          <Vertical flex={0} className='mb-2'>
            <BBDTag
              theme2={isDarkTheme}
              theme1={!isDarkTheme}
              className='text-truncate optimization-button'
              style={{
                border: '1px solid var(--theme-color-1)',
              }}
            >
              <FileDoneOutlined className='mr-1' />
              Pending
            </BBDTag>
          </Vertical>
        ) : optimizationState ? (
          <Vertical flex={0} className='mb-2'>
            <BBDTag
              theme2
              className='text-truncate optimization-button'
              style={{
                border: '1px solid var(--theme-color-2)',
              }}
            >
              <LoadingOutlined rotate={90} className='mr-1' />
              Optimizing
            </BBDTag>
          </Vertical>
        ) : (
          <Horizontal verticalCenter gap={4}>
            <TractorTrailerDisplay schedule={schedule} />
            <TrailerConfigSpan schedule={schedule} isMultiTruck={siteConfig?.multi_truck} />
          </Horizontal>
        )}
      </Horizontal>
    </>
  )
}

function LastUpdatedTooltip({ schedule, children }) {
  const lastUpdated = (
    <>
      <Texto style={{ color: 'inherit' }}>Last updated by: {schedule.equipment_updated_by} </Texto>
      <Texto style={{ color: 'inherit' }}>{formatters.DATE_TIME(schedule.equipment_updated_on)}</Texto>
    </>
  )
  return (
    <Tooltip title={schedule.equipment_updated_by ? lastUpdated : ''}>
      <div>{children}</div>
    </Tooltip>
  )
}

function TractorTrailerDisplay({ schedule }) {
  const tractor = schedule.current_tractor_number || schedule.driver_tractor_number
  const trailer = schedule.current_trailer_number || schedule.driver_trailer_number
  return (
    <Horizontal flex={1} gap={4}>
      {tractor && (
        <>
          <Tooltip title={schedule.current_tractor_number ? 'Current Tractor' : 'Driver Tractor'}>
            <div>
              <TractorIcon size={16} />
            </div>
          </Tooltip>
          <LastUpdatedTooltip schedule={schedule}>
            <Texto className='mr-2' category='p1'>
              {tractor}
            </Texto>
          </LastUpdatedTooltip>
        </>
      )}
      {trailer && (
        <>
          <Tooltip title={schedule.current_trailer_number ? 'Current Trailer' : 'Driver Trailer'}>
            <div>
              <TrailerIcon size={16} />
            </div>
          </Tooltip>
          <LastUpdatedTooltip schedule={schedule}>
            <Texto category='p1'>{trailer}</Texto>
          </LastUpdatedTooltip>
        </>
      )}
    </Horizontal>
  )
}

function TrailerConfigSpan({ schedule, isMultiTruck }) {
  const trailerConfig =
    schedule.current_trailer_config || schedule.driver_trailer_type || schedule.default_trailer_config
  const defaultConfig = schedule.default_trailer_config
  const isDifferent = trailerConfig && defaultConfig && trailerConfig !== defaultConfig
  return (
    <Texto
      className='trailer-config-span'
      style={{
        color: isDifferent ? 'var(--theme-warning)' : 'default',
      }}
    >
      {(() => {
        if (schedule.current_trailer_config || schedule.driver_trailer_type) {
          return isDifferent && isMultiTruck ? (
            <BBDTag warning>
              <Tooltip title="Not Driver's Default">
                <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <WarningOutlined />({trailerConfig})
                </span>
              </Tooltip>
            </BBDTag>
          ) : (
            <Tooltip title='Current Trailer Config'>
              <span>
                <Texto category='p1'>({trailerConfig})</Texto>
              </span>
            </Tooltip>
          )
        }
        if (schedule.default_trailer_config) {
          return (
            <Tooltip title='Driver Trailer Config'>
              <span>
                <Texto category='p1'>({trailerConfig})</Texto>
              </span>
            </Tooltip>
          )
        }
        if (isMultiTruck) {
          return (
            <Tooltip title='Trailer Config not available'>
              <span data-testid='na-config-tooltip'>
                <Texto category='p1'>(N/A)</Texto>
              </span>
            </Tooltip>
          )
        }
        return null
      })()}
    </Texto>
  )
}
function LinkSelect({ schedule, loadedMiles, totalMiles, shiftStart, shiftEnd, linkAction }) {
  const { formatDistance } = useFormatter()
  const formattedUnit = formatDistance(null, true)?.toUpperCase()
  return (
    <Row className='flex-1'>
      <Col span={12}>
        <GraviButton onClick={() => linkAction(schedule)} icon={<LinkOutlined />} theme2 buttonText='Link Schedule' />
      </Col>
      <Col className='vertical-flex justify-center' span={12}>
        <div className='truck-mileage'>{`${formattedUnit}: ${loadedMiles} | ${totalMiles}`}</div>
        <div className='shift-span text-uppercase'>
          {shiftStart} - {shiftEnd}
        </div>
      </Col>
    </Row>
  )
}

function StatusStamp({ isOnline, tripStatus, isCarrier, onCarrierSend, unsentIds, sendLoading, schedule }) {
  let Icon = null
  let color = ''
  let text = null
  const delayed = tripStatus.toLowerCase() === 'delayed'
  const onBreak = tripStatus.toLowerCase() === 'on break'

  if (onBreak) {
    color = 'var(--gray-600)'
    Icon = <IconClockPause size={16} data-testid='driver-break-icon' />
    text = ''
  } else if (delayed) {
    color = 'var(--theme-error)'
    Icon = <IconClockStop size={16} data-testid='driver-delay-icon' />
    text = ''
  } else if (tripStatus.toLowerCase() === 'not logged in') {
    color = 'var(--theme-error)'
    Icon = <IconClockExclamation size={16} data-testid='driver-not-login-icon' />
    text = 'Not Logged In'
  } else if (!isOnline && tripStatus.toLowerCase() === 'in progress') {
    color = 'var(--theme-warning)'
    Icon = <DisconnectOutlined />
    text = (
      <StatusStampOffline
        lastSeen={schedule.last_heartbeat}
        lastSeenTz={schedule.last_heartbeat_local}
        timeDisplayPreference={getTimeDisplayPreference()}
      />
    )
  } else if (tripStatus.toLowerCase() === 'in progress') {
    color = 'var(--theme-color-2)'
    Icon = <CompassFilled />
    text = 'In Progress'
  } else if (tripStatus.toLowerCase() === 'complete') {
    color = 'var(--theme-success)'
    Icon = <CheckCircleFilled />
    text = 'Complete'
  } else if (tripStatus.toLowerCase() === '') {
    color = 'var(--gray-300)'
    text = 'Not Started'
  }

  if (isCarrier) {
    color = 'var(--theme-color-4)'
    text = 'Carrier Order'
    Icon = <FlagOutlined />
  }

  return (
    <Tooltip title={text}>
      <div className='status-stamp' style={{ backgroundColor: color }}>
        <Vertical justifyContent='center' alignItems='center' style={{ gap: '0.5rem' }}>
          {Icon}
          {isCarrier && (
            <GraviButton
              icon={<SendOutlined />}
              theme1
              size='small'
              onClick={onCarrierSend}
              disabled={!unsentIds.length}
              loading={sendLoading}
            />
          )}
        </Vertical>
      </div>
    </Tooltip>
  )
}

function getDelayBreakPopoverTitle(driverDelay, isOnBreak, driverBreak) {
  return (
    <>
      <Horizontal alignItems='center'>
        <div
          style={{
            borderRadius: '100%',
            backgroundColor: isOnBreak ? 'var(--gray-600)' : 'var(--theme-error)',
            padding: '3px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '5px',
          }}
        >
          {isOnBreak ? (
            <IconClockPause fill={'var(--gray-600)'} color={'white'} className='m-1' />
          ) : (
            <IconClockStop fill={'var(--theme-error)'} color={'white'} />
          )}
        </div>
        <Vertical data-testid='duration'>
          <Texto appearance='bold' category='h6'>
            {isOnBreak
              ? `Break - Duration ${durationFormatter(driverBreak.minutes)}`
              : `Delay - Duration ${durationFormatter(driverDelay.minutes)}`}
          </Texto>
        </Vertical>
        <div data-testid='reported-date'>
          <Texto appearance='medium' category='p1'>
            {`Reported ${formatters.DATE_TIME(moment(isOnBreak ? driverBreak.date : driverDelay.date))}`}
          </Texto>
        </div>
      </Horizontal>
      {!isOnBreak && <hr className='action-block-hr' />}
    </>
  )
}

function getDelayPopoverContent(driverDelay, driverBreak, isOnBreak, canViewDwellReport, navigate) {
  return (
    <Vertical alignItems='space-between' className='mt-3'>
      <Horizontal justifyContent='space-between'>
        <Vertical>
          <Texto appearance='bold' category='h6'>
            Driver Status
          </Texto>
          {canViewDwellReport && !isOnBreak ? (
            <Horizontal alignItems='center' data-testid='delay-location' gap={8}>
              <Texto appearance='primary' category='p1'>
                {titleCase(driverDelay.step)}
              </Texto>
              <Button type='link' style={{ height: 16, padding: 0 }} onClick={() => navigate(`/admin/dwell`)}>
                {driverDelay.location}
              </Button>
            </Horizontal>
          ) : (
            <Texto appearance='primary' category='p1' className='mb-2' data-testid='delay-location'>
              {!isOnBreak
                ? `${titleCase(driverDelay.step)} ${driverDelay.location}`
                : `${titleCase(driverBreak?.route_action)} - ${driverBreak?.location}`}
            </Texto>
          )}
        </Vertical>
        {!isOnBreak && (
          <div>
            <Texto appearance='bold' category='h6'>
              Delay Reason
            </Texto>
            <Texto appearance='primary' category='p1' className='mb-2'>
              {titleCase(driverDelay.reason)}
            </Texto>
          </div>
        )}
      </Horizontal>
      {!isOnBreak && (
        <>
          <Texto appearance='bold' category='h6'>
            Estimated Delay Duration
          </Texto>
          <Texto appearance='primary' category='p1' className='mb-2'>
            {durationFormatter(driverDelay.minutes)}
          </Texto>
          {driverDelay.note && (
            <>
              <Texto appearance='bold' category='h6' data-testid='driver-note-heading'>
                Driver Note
              </Texto>
              <Texto appearance='primary' category='p1'>
                {driverDelay.note}
              </Texto>
            </>
          )}
        </>
      )}
    </Vertical>
  )
}
